import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AnalysisOptInModal } from './AnalysisOptInModal';
import { updateUserPreferences, UserPreferences } from '../../api/UserSettings';

interface AnalysisOptInContextType {
  showOptInModal: () => void;
  closeOptInModal: () => void;
}

const AnalysisOptInContext = createContext<AnalysisOptInContextType | undefined>(undefined);

interface AnalysisOptInProviderProps {
  children: ReactNode;
}

export const AnalysisOptInProvider: React.FC<AnalysisOptInProviderProps> = ({ children }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [enableFaceAnalysis, setEnableFaceAnalysis] = useState(false);
  const [enableImageAnalysis, setEnableImageAnalysis] = useState(false);

  const showOptInModal = () => {
    // Get current values from session storage
    const faceAnalysis = sessionStorage.getItem('enableFaceAnalysis') === 'true';
    const imageAnalysis = sessionStorage.getItem('enableImageAnalysis') === 'true';
    
    setEnableFaceAnalysis(faceAnalysis);
    setEnableImageAnalysis(imageAnalysis);
    setModalOpen(true);
  };

  const closeOptInModal = () => {
    setModalOpen(false);
  };

  const handleSaveSettings = async (settings: { 
    enableFaceAnalysis: boolean; 
    enableImageAnalysis: boolean 
  }) => {
    const result = await updateUserPreferences(settings);
    
    // Update local state with the values from the API response
    if (result.userAttributes.enableFaceAnalysis !== undefined) {
      setEnableFaceAnalysis(result.userAttributes.enableFaceAnalysis);
    }
    if (result.userAttributes.enableImageAnalysis !== undefined) {
      setEnableImageAnalysis(result.userAttributes.enableImageAnalysis);
    }
  };

  return (
    <AnalysisOptInContext.Provider value={{ showOptInModal, closeOptInModal }}>
      {children}
      <AnalysisOptInModal
        opened={modalOpen}
        onClose={closeOptInModal}
        enableFaceAnalysis={enableFaceAnalysis}
        // enableImageAnalysis={enableImageAnalysis}
        onSaveSettings={handleSaveSettings}
      />
    </AnalysisOptInContext.Provider>
  );
};

export const useAnalysisOptIn = (): AnalysisOptInContextType => {
  const context = useContext(AnalysisOptInContext);
  if (context === undefined) {
    throw new Error('useAnalysisOptIn must be used within an AnalysisOptInProvider');
  }
  return context;
};
